/**
 * LicencesPage — Gestion du cycle de vie des clés de licence (admin).
 *
 * Opérations supportées par le SDK / backend :
 *   - activateLicense(payload)   → POST   /api/v1/admin/licenses
 *   - getStatus(license_key_hash) → GET    /api/v1/admin/licenses/{hash}
 *   - revokeLicense(hash)         → DELETE /api/v1/admin/licenses/{hash}
 *
 * Note : le backend n'expose pas de « liste globale » des licences. La page
 * travaille donc sur un ensemble de licences soit activées dans la session,
 * soit recherchées par leur hash. (Distinct de /premium, qui gère l'accès
 * Premium par utilisateur.)
 */
import React, { useState } from 'react';
import { KeyRound, Plus, RotateCcw, Search, ShieldX } from 'lucide-react';
import {
  activateLicense,
  getStatus,
  revokeLicense,
  type LicensePayload,
  type LicenseRow,
  type LicenseStatus,
} from '@assistia/sdk';
import { useAuth } from '../context/AuthContext.tsx';

// ── Helpers ─────────────────────────────────────────────────────────────────

type Entry =
  | { hash: string; revoked: true }
  | { hash: string; revoked: false; status: LicenseStatus };

function computeState(entry: Entry): 'revoked' | 'expired' | 'active' {
  if (entry.revoked) return 'revoked';
  const expired = new Date(entry.status.valid_until).getTime() < Date.now();
  if (expired) return 'expired';
  const hasEnabled = Object.values(entry.status.features).some((f) => f.enabled);
  return hasEnabled ? 'active' : 'revoked';
}

const STATE_BADGE: Record<'revoked' | 'expired' | 'active', string> = {
  active: 'bg-green-500/15 text-green-600',
  expired: 'bg-muted text-muted-foreground',
  revoked: 'bg-destructive/15 text-destructive',
};

// ── Page ────────────────────────────────────────────────────────────────────

export default function LicencesPage(): React.ReactElement {
  const { getToken } = useAuth();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Recherche par hash
  const [lookupHash, setLookupHash] = useState('');

  // Formulaire d'activation
  const [showActivate, setShowActivate] = useState(false);

  const upsert = (entry: Entry) =>
    setEntries((prev) => [entry, ...prev.filter((e) => e.hash !== entry.hash)]);

  const handleLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    const hash = lookupHash.trim();
    if (!hash) return;
    setBusy(true);
    setError(null);
    try {
      const res = await getStatus(hash, getToken());
      if (res.blocked || !res.data) {
        setError(res.error?.message || 'Recherche bloquée ou invalide');
        return;
      }
      upsert({ hash, revoked: false, status: res.data });
      setLookupHash('');
    } catch (err) {
      setError(`Recherche échouée : ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (hash: string) => {
    if (!confirm('Révoquer cette licence ? Cette action est irréversible.')) return;
    setBusy(true);
    setError(null);
    try {
      await revokeLicense(hash, getToken());
      upsert({ hash, revoked: true });
    } catch (err) {
      setError(`Révocation échouée : ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const handleActivated = (row: LicenseRow) => {
    // activateLicense renvoie la ligne complète (avec le hash) → on l'affiche
    upsert({
      hash: row.license_key_hash,
      revoked: false,
      status: {
        id: row.id,
        user_id: row.user_id,
        features: row.features,
        valid_until: row.valid_until,
        created_at: row.created_at,
        updated_at: row.updated_at,
      },
    });
    setShowActivate(false);
  };

  return (
    <div className="space-y-6">
      {showActivate && (
        <ActivateModal
          onClose={() => setShowActivate(false)}
          onActivated={handleActivated}
          onError={setError}
          token={getToken()}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Licences (clés)</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Cycle de vie des clés de licence : activation, état, révocation.
          </p>
        </div>
        <button
          onClick={() => setShowActivate(true)}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground transition-colors hover:bg-primary/90"
        >
          <Plus size={16} />
          Activer une licence
        </button>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">{error}</div>
      )}

      {/* Recherche par hash */}
      <form onSubmit={handleLookup} className="flex gap-2">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-2.5 text-muted-foreground" />
          <input
            value={lookupHash}
            onChange={(e) => setLookupHash(e.target.value)}
            placeholder="Rechercher par hash de clé de licence…"
            className="w-full rounded-md border border-input bg-background py-2 pl-9 pr-3 text-sm"
          />
        </div>
        <button
          type="submit"
          disabled={busy || !lookupHash.trim()}
          className="rounded-md border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-muted disabled:opacity-50"
        >
          Rechercher
        </button>
      </form>

      {/* Tableau */}
      {entries.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          <KeyRound size={20} className="mx-auto mb-2 text-muted-foreground/60" />
          Aucune licence affichée. Activez une licence ou recherchez-la par son hash.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-card shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">Utilisateur</th>
                <th className="px-4 py-3 font-medium">État</th>
                <th className="px-4 py-3 font-medium">Valide jusqu'au</th>
                <th className="px-4 py-3 font-medium">Features</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => {
                const state = computeState(entry);
                return (
                  <tr key={entry.hash} className="border-t border-border hover:bg-muted/30">
                    <td className="px-4 py-3 font-medium text-foreground">
                      {entry.revoked ? '—' : entry.status.user_id}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${STATE_BADGE[state]}`}>
                        {state}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {entry.revoked ? '—' : new Date(entry.status.valid_until).toLocaleString('fr-FR')}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {entry.revoked
                        ? '—'
                        : Object.entries(entry.status.features)
                            .filter(([, f]) => f.enabled)
                            .map(([name]) => name)
                            .join(', ') || '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleRevoke(entry.hash)}
                        disabled={busy || entry.revoked}
                        title="Révoquer"
                        className="inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-40"
                      >
                        <ShieldX size={13} />
                        Révoquer
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="flex items-center gap-1 text-xs text-muted-foreground">
        <RotateCcw size={12} />
        Le backend n'expose pas de liste globale : seules les licences activées ici ou recherchées par hash sont affichées.
      </p>
    </div>
  );
}

// ── Modale d'activation ──────────────────────────────────────────────────────

function ActivateModal({
  onClose,
  onActivated,
  onError,
  token,
}: {
  onClose: () => void;
  onActivated: (row: LicenseRow) => void;
  onError: (msg: string) => void;
  token: string | null;
}): React.ReactElement {
  const [userId, setUserId] = useState('');
  const [licenseKey, setLicenseKey] = useState('');
  const [featureName, setFeatureName] = useState('agentic');
  const [days, setDays] = useState(365);
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const validUntil = new Date(Date.now() + days * 86_400_000).toISOString();
    const payload: LicensePayload = {
      user_id: userId.trim(),
      license_key: licenseKey.trim(),
      features: { [featureName.trim()]: { enabled: true, valid_until: validUntil } },
      valid_until: validUntil,
    };
    try {
      const res = await activateLicense(payload, token);
      if (res.blocked || !res.data) {
        onError(res.error?.message || 'Activation bloquée ou invalide');
        return;
      }
      onActivated(res.data);
    } catch (err) {
      onError(`Activation échouée : ${(err as Error).message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md space-y-4 rounded-lg border border-border bg-card p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-foreground">Activer une licence</h2>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="mb-1 block text-sm font-medium">User ID</label>
            <input
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              required
              placeholder="UUID utilisateur"
              className="w-full rounded border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Clé de licence</label>
            <input
              value={licenseKey}
              onChange={(e) => setLicenseKey(e.target.value)}
              required
              placeholder="Clé brute"
              className="w-full rounded border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="mb-1 block text-sm font-medium">Feature</label>
              <select
                value={featureName}
                onChange={(e) => setFeatureName(e.target.value)}
                className="w-full rounded border border-input bg-background px-3 py-2 text-sm"
              >
                {['agentic', 'bricks', 'scenarios'].map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </div>
            <div className="w-28">
              <label className="mb-1 block text-sm font-medium">Durée (j)</label>
              <input
                type="number"
                min={1}
                value={days}
                onChange={(e) => setDays(Number(e.target.value) || 1)}
                className="w-full rounded border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-border px-4 py-2 text-sm transition-colors hover:bg-muted"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="rounded bg-primary px-4 py-2 text-sm text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
            >
              {loading ? 'Activation…' : 'Activer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
